import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Card, Space, Button, Modal, Descriptions, Select, Image, Spin, Tag, Typography, Popconfirm } from 'antd';
import { EyeOutlined, LinkOutlined, ShopOutlined, CloudDownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { unmappedOrdersAPI, productAPI, customizationAPI, freightAPI } from '../api';
import ProductSearchSelector from '../utils/ProductSearchSelector';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Paragraph } = Typography;
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const getPlatformIcon = (platform) => {
  switch ((platform || '').toLowerCase()) {
    case 'shopify':
      return <ShopifyIcon />;
    case 'woocommerce':
      return <WooCommerceIcon />;
    default:
      return <ShopOutlined />;
  }
};

const renderPlatformTag = (platform) => {
  if (!platform) {
    return '-';
  }

  let style = {};
  const lowerPlatform = (platform || '').toLowerCase();
  if (lowerPlatform === 'woocommerce') {
    style = { background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' };
  } else if (lowerPlatform === 'shopify') {
    style = { backgroundColor: '#f0f9f0', borderColor: '#96bf48', color: '#2d5a1d' };
  } else {
    style = { backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: '#000000' };
  }

  return (
    <Tag
      icon={getPlatformIcon(platform)}
      style={style}
    >
      {platform.toUpperCase()}
    </Tag>
  );
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const UnmappedOrders = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '50'],
    showTotal: (total, range) => {
      if (!Array.isArray(range) || range.length < 2) {
        return `${total} orders`;
      }
      return `${range[0]}-${range[1]} of ${total} orders`;
    },
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // Customer info modal state
  const [customerModalVisible, setCustomerModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Product mapping modal state
  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [selectedUnmappedOrder, setSelectedUnmappedOrder] = useState(null);
  const [showProductSelector, setShowProductSelector] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariantPath, setSelectedVariantPath] = useState([]);
  const [selectedFreightId, setSelectedFreightId] = useState(null);
  const [selectedCustomizationId, setSelectedCustomizationId] = useState(null);
  
  // Options data
  const [freightChannels, setFreightChannels] = useState([]);
  const [customizations, setCustomizations] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchUnmappedOrders = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await unmappedOrdersAPI.getAll({
        page,
        page_size: pageSize,
      });
      const responseData = response.data || {};
      const data = Array.isArray(responseData.data) ? responseData.data : [];
      setRecords(data);
      setPagination((prev) => ({
        ...prev,
        current: responseData.page || page,
        pageSize: responseData.page_size || pageSize,
        total: responseData.total ?? data.length,
      }));
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Failed to load unmapped orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchUnmappedOrders();
    fetchFreightChannels();
    fetchCustomizations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFreightChannels = async () => {
    try {
      const response = await freightAPI.getAll();
      setFreightChannels(response.data || []);
    } catch (error) {
      
    }
  };

  const fetchCustomizations = async () => {
    try {
      const response = await customizationAPI.getAll();
      setCustomizations(response.data || []);
    } catch (error) {
      
    }
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchUnmappedOrders(current, pageSize);
  };

  const handleNavigateToStoreImport = () => {
    navigate('/dropshipper/my-stores', { state: { importGuide: true } });
  };

  const handleDeleteOrder = async (record) => {
    const orderId = record.id;
    const targetId = String(orderId);
    setDeletingId(targetId);
    try {
      await unmappedOrdersAPI.delete(targetId);
      message.success('Unmapped order deleted');
      setSelectedRowKeys((prev) => prev.filter((key) => key !== targetId));
      fetchUnmappedOrders();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete unmapped order');
    } finally {
      setDeletingId(null);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    setBatchDeleting(true);
    try {
      const ids = selectedRowKeys.map((key) => String(key));
      await unmappedOrdersAPI.batchDelete(ids);
      message.success('Selected unmapped orders deleted');
      setSelectedRowKeys([]);
      fetchUnmappedOrders();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete selected unmapped orders');
    } finally {
      setBatchDeleting(false);
    }
  };

  // View customer info
  const handleViewCustomer = (record) => {
    setSelectedCustomer(record);
    setCustomerModalVisible(true);
  };

  const handleCloseCustomerModal = () => {
    setCustomerModalVisible(false);
    setSelectedCustomer(null);
  };

  // Start product mapping
  const handleFindProduct = (record) => {
    setSelectedUnmappedOrder(record);
    setShowProductSelector(true);
    setSelectedProduct(null);
    setSelectedVariantPath([]);
    setSelectedFreightId(null);
    setSelectedCustomizationId(null);
    setMappingModalVisible(true);
  };

  // Handle product selection from ProductSearchSelector
  const handleProductSelect = async (productId, productData) => {
    try {
      setProductLoading(true);
      // If productData is not provided, fetch it
      let product = productData;
      if (!product) {
        const response = await productAPI.getById(productId);
        product = response.data;
      }
      setSelectedProduct(product);
      setShowProductSelector(false);
      
      // Reset selections
      setSelectedVariantPath([]);
      setSelectedFreightId(null);
      setSelectedCustomizationId(null);
    } catch (error) {
      message.error('Failed to load product details');
      
    } finally {
      setProductLoading(false);
    }
  };

  // Change product selection
  const handleChangeProduct = () => {
    setShowProductSelector(true);
    setSelectedProduct(null);
    setSelectedVariantPath([]);
  };

  // Get variant options from product - adapted from ProductDetailPage
  const getVariantOptions = (variants) => {
    if (!variants || !Array.isArray(variants)) return [];
    return variants.map(v => ({
      variant_path: v.variant_path || [],
      SKU: v.SKU,
      price: v.price,
      currency: v.currency || 'USD',
      weight: v.weight,
      stock: v.stock,
      images: v.images || [],
    }));
  };

  // Parse variant levels - adapted from ProductDetailPage
  const parseVariantLevels = (variants, variantTypes) => {
    if (!variants || !Array.isArray(variants)) return [];
    if (!variantTypes || variantTypes.length === 0) return [];

    const levels = [];
    const numLevels = variantTypes.length;

    for (let i = 0; i < numLevels; i++) {
      const valuesSet = new Set();
      variants.forEach(variant => {
        const path = variant.variant_path || [];
        if (path.length > i) {
          valuesSet.add(path[i]);
        }
      });
      levels.push({
        type: variantTypes[i],
        values: Array.from(valuesSet),
      });
    }

    return levels;
  };

  // Check if variant value is available
  const isVariantValueAvailable = (value, levelIndex) => {
    if (!selectedProduct) return false;
    
    const currentPath = [...selectedVariantPath];
    currentPath[levelIndex] = value;
    
    const variants = getVariantOptions(selectedProduct.variants);
    
    return variants.some(variant => {
      const variantPath = variant.variant_path || [];
      for (let i = 0; i <= levelIndex; i++) {
        if (variantPath[i] !== currentPath[i]) {
          return false;
        }
      }
      return true;
    });
  };

  // Handle variant value click
  const handleVariantValueClick = (value, levelIndex) => {
    const newPath = [...selectedVariantPath];
    newPath[levelIndex] = value;
    newPath.length = levelIndex + 1;
    setSelectedVariantPath(newPath);
  };

  // Get current variant details
  const getCurrentVariant = () => {
    if (!selectedProduct || selectedVariantPath.length === 0) return null;
    
    const variants = getVariantOptions(selectedProduct.variants);
    return variants.find(v => {
      const vPath = v.variant_path || [];
      return vPath.length === selectedVariantPath.length &&
             vPath.every((val, idx) => val === selectedVariantPath[idx]);
    });
  };

  // Get variant images
  const getVariantImages = () => {
    const currentVariant = getCurrentVariant();
    if (currentVariant && currentVariant.images && currentVariant.images.length > 0) {
      return currentVariant.images;
    }
    return [];
  };

  // Submit order creation
  const handleSubmitOrder = async () => {
    if (!selectedProduct) {
      message.error('Please select a product');
      return;
    }

    const variantLevels = parseVariantLevels(selectedProduct.variants, selectedProduct.variant_types);
    if (variantLevels.length > 0 && selectedVariantPath.length !== variantLevels.length) {
      message.error('Please select all variant options');
      return;
    }

    if (!selectedFreightId) {
      message.error('Please select a freight channel');
      return;
    }

    try {
      setSubmitting(true);
      await unmappedOrdersAPI.createOrder(selectedUnmappedOrder.id, {
        unmapped_order_id: selectedUnmappedOrder.id,
        product_id: selectedProduct.id,
        variant_path: selectedVariantPath.length > 0 ? selectedVariantPath : null,
        freight_id: selectedFreightId,
        customization_id: selectedCustomizationId || null,
      });
      
      message.success('Order created successfully!');
      setMappingModalVisible(false);
      fetchUnmappedOrders(); // Refresh list
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to create order');
      
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (value, record) => {
        const displayValue = value || record.product_description || '-';
        const productTextStyle = {
          wordBreak: 'break-word',
          margin: 0,
        };
        const textNode = record.url ? (
          <a href={record.url} target="_blank" rel="noopener noreferrer">
            {displayValue}
          </a>
        ) : (
          <span style={productTextStyle}>{displayValue}</span>
        );
        const linkContent = (
          <Paragraph
            ellipsis={{
              rows: 3,
              tooltip: displayValue,
            }}
            style={productTextStyle}
          >
            {textNode}
          </Paragraph>
        );

        return (
          <Space size="middle">
            {record.img_url ? (
              <img
                src={record.img_url}
                alt={value || 'Product image'}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 6,
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  background: 'rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(0,0,0,0.35)',
                  fontSize: 12,
                }}
              >
                N/A
              </div>
            )}
            {linkContent}
          </Space>
        );
      },
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 140,
      render: (value) => renderPlatformTag(value),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: 'Quantity',
      dataIndex: 'count',
      key: 'count',
      width: 80,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (value, record) => {
        const country = record.dest_country ? ` (${record.dest_country})` : '';
        return value ? `${value}${country}` : '-';
      },
    },
    {
      title: 'Actions',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ alignItems: 'flex-start' }}>
          <Space size="small">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewCustomer(record)}
            >
              Info
            </Button>
            <Popconfirm
              title="Delete this unmapped order?"
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: deletingId === String(record.id) }}
              onConfirm={() => handleDeleteOrder(record)}
              icon={<ExclamationCircleOutlined />}
            >
              <Button
                size="small"
                danger
                loading={deletingId === String(record.id)}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
          <Button
            size="small"
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => handleFindProduct(record)}
            disabled={record.mapped}
          >
            Find Product
          </Button>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys.map((key) => String(key))),
  };

  return (
    <Card
      title="Unmapped Orders"
      extra={
        <Button
          icon={<CloudDownloadOutlined />}
          onClick={handleNavigateToStoreImport}
        >
          Import orders from my store
        </Button>
      }
    >
      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Popconfirm
            title={`Delete ${selectedRowKeys.length} unmapped orders?`}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: batchDeleting }}
            onConfirm={handleBatchDelete}
            icon={<ExclamationCircleOutlined />}
          >
            <Button danger loading={batchDeleting}>
              Batch Delete ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
        </div>
      )}
      <Table
        rowKey={(record) => record.id}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={records}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
      />

      {/* Customer Info Modal */}
      <Modal
        title="Information"
        open={customerModalVisible}
        onCancel={handleCloseCustomerModal}
        footer={[
          <Button key="close" onClick={handleCloseCustomerModal}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedCustomer && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Platform">
              {renderPlatformTag(selectedCustomer.platform)}
            </Descriptions.Item>
            <Descriptions.Item label="Store">
              {selectedCustomer.third_party_info?.shop_domain ? (
                <a
                  href={`https://${selectedCustomer.third_party_info.shop_domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedCustomer.third_party_info.shop_domain}
                </a>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="External Order">
              {selectedCustomer.third_party_info?.shopify_order_number || selectedCustomer.channel_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {formatDateTime(selectedCustomer.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Customer Name">
              {selectedCustomer.customer_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedCustomer.customer_email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {selectedCustomer.customer_phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Shipping Address">
              {selectedCustomer.shipping_address || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Destination Country">
              {selectedCustomer.dest_country || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Product Mapping Modal */}
      <Modal
        title="Map to our product"
        open={mappingModalVisible}
        onCancel={() => setMappingModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {showProductSelector ? (
          <div>
            <h3 style={{ marginBottom: 16 }}>Select a Product</h3>
            <ProductSearchSelector
              multiple={false}
              onSelect={handleProductSelect}
            />
          </div>
        ) : (
          <Spin spinning={productLoading}>
            {selectedProduct && (
              <div>
                {/* Selected Product Display */}
                <Card size="small" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {selectedProduct.images && selectedProduct.images.length > 0 ? (
                      <Image
                        src={`${API_BASE_URL}/products/${selectedProduct.id}/images/${selectedProduct.images[0].id}`}
                        width={80}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 80,
                          height: 80,
                          backgroundColor: '#f0f0f0',
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        No Image
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {selectedProduct.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        Product ID: {selectedProduct.id}
                      </div>
                    </div>
                    <Button onClick={handleChangeProduct} size="small">
                      Change Product
                    </Button>
                  </div>
                </Card>

                {/* Variant Selector */}
                {(() => {
                  const variantLevels = parseVariantLevels(
                    selectedProduct.variants,
                    selectedProduct.variant_types
                  );

                  if (variantLevels.length > 0) {
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <h4>Select SKU / Variant:</h4>
                        {variantLevels.map((level, levelIndex) => (
                          <div key={levelIndex} style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontWeight: 500 }}>
                              {level.type}:
                            </div>
                            <Space wrap>
                              {level.values.map((value) => {
                                const isSelected = selectedVariantPath[levelIndex] === value;
                                const isAvailable = isVariantValueAvailable(value, levelIndex);

                                return (
                                  <Button
                                    key={value}
                                    type={isSelected ? 'primary' : 'default'}
                                    disabled={!isAvailable}
                                    onClick={() => handleVariantValueClick(value, levelIndex)}
                                    style={{
                                      opacity: isAvailable ? 1 : 0.5,
                                    }}
                                  >
                                    {value}
                                  </Button>
                                );
                              })}
                            </Space>
                          </div>
                        ))}

                        {/* Show variant images if available */}
                        {(() => {
                          const variantImages = getVariantImages();
                          if (variantImages.length > 0) {
                            return (
                              <div style={{ marginTop: 16 }}>
                                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                                  Variant Images:
                                </div>
                                <Space>
                                  {variantImages.map((imageId, idx) => (
                                    <Image
                                      key={idx}
                                      src={`${API_BASE_URL}/products/${selectedProduct.id}/images/${imageId}`}
                                      width={100}
                                      height={100}
                                      style={{ objectFit: 'cover', borderRadius: 4 }}
                                    />
                                  ))}
                                </Space>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Freight Channel Selector */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Freight Channel: <span style={{ color: 'red' }}>*</span>
                  </div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select freight channel"
                    value={selectedFreightId}
                    onChange={setSelectedFreightId}
                  >
                    {freightChannels.map((freight) => (
                      <Option key={freight.id} value={freight.id}>
                        {freight.name}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Customization Selector */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Customization (Optional):
                  </div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select customization (optional)"
                    value={selectedCustomizationId}
                    onChange={setSelectedCustomizationId}
                    allowClear
                  >
                    {customizations.map((custom) => (
                      <Option key={custom.id} value={custom.id}>
                        {custom.customization_name}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Submit Button */}
                <div style={{ textAlign: 'right', marginTop: 24 }}>
                  <Space>
                    <Button onClick={() => setMappingModalVisible(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleSubmitOrder}
                      loading={submitting}
                    >
                      Create Order
                    </Button>
                  </Space>
                </div>
              </div>
            )}
          </Spin>
        )}
      </Modal>
    </Card>
  );
};

export default UnmappedOrders;
